import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Clock, ArrowRight } from "lucide-react";
import DOMPurify from "dompurify";
import api from "@/lib/api";
import { formatDateAU } from "@/lib/formatDateAU";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { ArticleImage } from "@/components/ArticleImage";
import { BLOG } from "@/constants/testIds";
import { useSEO, SEO_DEFAULTS } from "@/lib/useSEO";

export default function Article() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useSEO({
    title: article ? article.title : "Knowledge Hub",
    description: article ? article.excerpt : "Plain-English water knowledge from our lab technicians.",
    url: `${SEO_DEFAULTS.baseUrl}/knowledge-hub/${slug}`,
    image: article?.cover || SEO_DEFAULTS.image,
    type: "article",
    jsonLd: article ? [
      {
        "@context": "https://schema.org",
        "@type":    "Article",
        headline:   article.title,
        description: article.excerpt,
        image:      article.cover || SEO_DEFAULTS.image,
        datePublished: article.published_at || article.created_at,
        dateModified:  article.updated_at || article.published_at || article.created_at,
        author: { "@type": "Organization", name: "Aware Water Testing" },
        publisher: {
          "@type": "Organization",
          name: "Aware Water Testing",
          logo: { "@type": "ImageObject",
                  url: "https://customer-assets.emergentagent.com/job_pristine-lab/artifacts/jhugp9s5_image.png" },
        },
        mainEntityOfPage: `${SEO_DEFAULTS.baseUrl}/knowledge-hub/${slug}`,
      },
      {
        "@context": "https://schema.org",
        "@type":    "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home",
            item: `${SEO_DEFAULTS.baseUrl}/` },
          { "@type": "ListItem", position: 2, name: "Knowledge Hub",
            item: `${SEO_DEFAULTS.baseUrl}/knowledge-hub` },
          { "@type": "ListItem", position: 3, name: article.title,
            item: `${SEO_DEFAULTS.baseUrl}/knowledge-hub/${slug}` },
        ],
      },
    ] : null,
  });

  useEffect(() => {
    setLoading(true);
    api.get(`/articles/${slug}`)
      .then(({ data }) => setArticle(data))
      .catch(() => navigate("/knowledge-hub"))
      .finally(() => setLoading(false));
  }, [slug, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <main className="pt-32 max-w-3xl mx-auto px-4 text-slate-500">Loading article...</main>
        <Footer />
      </div>
    );
  }

  if (!article) return null;

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="pt-28 lg:pt-32 pb-20">
        <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/knowledge-hub" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-900 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Knowledge Hub
          </Link>

          <div className="mt-7 text-[11px] font-bold uppercase tracking-[0.2em] text-cyan-600">{article.category}</div>
          <h1 className="font-display mt-2 text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-[1.1]">{article.title}</h1>
          <div className="mt-4 flex items-center gap-4 text-sm text-slate-500">
            <span className="inline-flex items-center gap-1"><Clock className="w-4 h-4" /> {article.read_time} min read</span>
            <span>·</span>
            <span>{formatDateAU(article.published_at)}</span>
          </div>

          <div className="mt-8 rounded-2xl overflow-hidden">
            <ArticleImage src={article.cover} alt={article.title} className="w-full h-72 object-cover" />
          </div>

          <div
            data-testid={BLOG.articleBody}
            className="prose-article mt-10 text-slate-700 leading-[1.8] text-[17px]"
            /* Article bodies are written by admins (blog_seed.py) — not user
               input — so XSS risk is low. We still pass through DOMPurify
               as defence-in-depth so even a future content-management
               surface can't accidentally inject script tags. */
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.body_html || "") }}
          />

          <div className="mt-14 p-7 lg:p-10 rounded-3xl bg-slate-900 text-white">
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-400">Want answers about your water?</div>
            <h3 className="font-display mt-2 text-2xl lg:text-3xl font-bold text-white">Get a professional lab analysis</h3>
            <p className="mt-3 text-slate-300">Every booking includes a Certificate of Analysis + plain-English summary report.</p>
            <Link to="/book-water-test" className="btn-aqua mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold">
              Book online — one flat all-inclusive price <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </article>
      </main>
      <Footer />
      <style>{`
        .prose-article h2 { font-family: 'Outfit', sans-serif; font-weight: 700; color: #0F172A; font-size: 1.6rem; margin: 2.2rem 0 1rem; line-height: 1.2; }
        .prose-article p { margin-bottom: 1.2rem; }
        .prose-article ul, .prose-article ol { margin: 1rem 0 1.4rem; padding-left: 1.4rem; }
        .prose-article li { margin-bottom: 0.5rem; }
        .prose-article strong { color: #0F172A; font-weight: 600; }
      `}</style>
    </div>
  );
}
